import React, { useEffect, useState, useReducer } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
  Box
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import PageHelpGuide from "../../components/PageHelpGuide";
import { AUDIT_HELP_DATA } from "../../constants/pageHelp/auditHelp";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { Visibility, Search, Refresh as RefreshIcon, Http as HttpIcon, Close as CloseIcon, HelpOutline as HelpOutlineIcon } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import Collapse from "@material-ui/core/Collapse";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import { format, parseISO } from "date-fns";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  cyanButton: {
    backgroundColor: '#00b0ff',
    color: '#fff',
    borderRadius: '4px',
    textTransform: 'none',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0091ea',
      boxShadow: '0 4px 12px rgba(0, 176, 255, 0.4)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32 !important" : theme.palette.background.paper,
  },
  headerWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginTop: 28,
    marginBottom: 30,
    paddingLeft: 20,
    paddingRight: 20,
  },
  pageTitle: {
    fontWeight: 700,
    letterSpacing: "-0.5px",
    color: theme.palette.type === "dark" ? "#FFF" : theme.palette.primary.main,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  jsonContainer: {
    backgroundColor: theme.palette.type === "dark" ? "#121418" : "#f8f9fa",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    padding: "12px 16px",
    borderRadius: "8px",
    fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    maxHeight: "260px",
    overflowY: "auto",
    color: theme.palette.type === "dark" ? "#64dfdf" : "#0077b6",
    ...theme.scrollbarStyles,
  },
  statusSuccess: {
    color: "green",
    fontWeight: "bold",
  },
  statusError: {
    color: "red",
    fontWeight: "bold",
  },
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  helpBox: {
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
    }`,
    padding: theme.spacing(1.5, 3),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_LOGS") {
    return Array.isArray(action.payload) ? action.payload : [];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state || [];
};

const WebhookLogs = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [showHelp, setShowHelp] = useState(false);
  const [logs, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [reload, setReload] = useState(false);
  
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchLogs = async () => {
      try {
        const { data } = await api.get("/webhook-logs", {
          params: { pageNumber, perPage, searchParam },
        });
        dispatch({ type: "LOAD_LOGS", payload: data.webhookLogs || [] });
        setCount(data.count || 0);
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchLogs();
  }, [pageNumber, perPage, searchParam, reload]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const handleOpenModal = (log) => {
    setSelectedLog(log);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
    setModalOpen(false);
    setShowHelp(false);
  };

  const formatJson = (jsonStr) => {
    try {
      const obj = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return String(jsonStr || "");
    }
  };

  return (
    <MainContainer>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <div className={classes.headerWrapper}>
          <Box display="flex" alignItems="center" style={{ gap: '14px' }}>
            <PageHelpGuide
              helpData={AUDIT_HELP_DATA}
              tooltipText="Guía y Ayuda de Auditoría (Haz clic para ver)"
            />
            <Typography variant="h5" className={classes.pageTitle}>
              {i18n.t("common.webhookLogs.title")}
            </Typography>
          </Box>
          <MainHeaderButtonsWrapper>
            <TextField
              placeholder={i18n.t("common.webhookLogs.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              variant="outlined"
              className={classes.applyButton}
              startIcon={<RefreshIcon style={{ fontSize: 18 }} />}
              onClick={() => {
                dispatch({ type: "RESET" });
                if (pageNumber === 1) {
                  setReload(!reload);
                } else {
                  setPageNumber(1);
                }
              }}
            >
              {i18n.t("common.webhookLogs.buttons.update")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
        <TableContainer className={classes.scrollArea}>
          <Table size="small" stickyHeader>
            <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.id")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.date")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.name")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.phone")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.origin")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.method")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.url")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.status")}</TableCell>
              <TableCell className={classes.tableHeader} align="center">{i18n.t("common.webhookLogs.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {(Array.isArray(logs) ? logs : []).map((log) => (
                <TableRow key={log.id}>
                  <TableCell align="center">{log.id}</TableCell>
                  <TableCell align="center">
                    <div>{format(parseISO(log.createdAt), "dd/MM/yyyy")}</div>
                    <div>{format(parseISO(log.createdAt), "HH:mm")}</div>
                  </TableCell>
                  <TableCell align="center">{log.ticket?.contact?.name || "-"}</TableCell>
                  <TableCell align="center">{log.phoneNumber || "-"}</TableCell>
                  <TableCell align="center">{log.flowName}</TableCell>
                  <TableCell align="center">{log.method}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={log.url || ""}>
                      <span>{log.url && log.url.length > 30 ? log.url.substring(0, 30) + '...' : log.url}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <span className={log.status >= 200 && log.status < 300 ? classes.statusSuccess : classes.statusError}>
                      {log.status}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenModal(log)}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRow><TableCell colSpan={8} align="center">{i18n.t("common.webhookLogs.loading")}</TableCell></TableRow>}
            </>
          </TableBody>
        </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 15, 20]}
          component="div"
          count={count}
          rowsPerPage={perPage}
          page={pageNumber - 1}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={i18n.t("common.webhookLogs.table.rowsPerPage")}
        />
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogHeader}>
          <h2>
            <HttpIcon style={{ marginRight: 10, fontSize: 24, color: "#00b4d8" }} />
            {i18n.t("common.webhookLogs.modal.title")} #{selectedLog?.id}
          </h2>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={() => setShowHelp((prev) => !prev)}
              title="Ayuda / Información"
              style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
            >
              BotMix | Integraciones
            </Typography>
            <IconButton size="small" onClick={handleCloseModal} aria-label="Cerrar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </div>

        <Collapse in={showHelp}>
          <Box className={classes.helpBox}>
            <Typography
              variant="body2"
              style={{
                color: theme.palette.type === "dark" ? "#90e0ef" : "#0077b6",
                lineHeight: 1.5,
              }}
            >
              {i18n.t("common.webhookLogs.modal.helpSummary")}
            </Typography>
          </Box>
        </Collapse>

        <DialogContent dividers style={{ padding: "20px 24px" }}>
          <Box mb={2}>
            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <b>{i18n.t("common.webhookLogs.modal.url")}</b>{" "}
              <span style={{ wordBreak: "break-all", opacity: 0.9 }}>{selectedLog?.url}</span>
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 4 }}>
              <b>{i18n.t("common.webhookLogs.modal.status")}</b>{" "}
              <span
                style={{
                  fontWeight: 700,
                  color: selectedLog?.status >= 200 && selectedLog?.status < 300 ? "#2ecc71" : "#e74c3c"
                }}
              >
                {selectedLog?.status}
              </span>
            </Typography>
            <Typography variant="body2">
              <b>{i18n.t("common.webhookLogs.modal.phone")}</b>{" "}
              <span style={{ opacity: 0.9 }}>{selectedLog?.phoneNumber || "-"}</span>
            </Typography>
          </Box>

          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 6 }}>
            {i18n.t("common.webhookLogs.modal.payload")}
          </Typography>
          <pre className={classes.jsonContainer}>
            {selectedLog ? formatJson(selectedLog.payload) : ""}
          </pre>

          <Typography variant="subtitle2" style={{ fontWeight: 600, marginTop: 16, marginBottom: 6 }}>
            {i18n.t("common.webhookLogs.modal.response")}
          </Typography>
          <pre className={classes.jsonContainer}>
            {selectedLog ? formatJson(selectedLog.response) : ""}
          </pre>
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={handleCloseModal} className={classes.applyButton}>
            {i18n.t("common.webhookLogs.buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default WebhookLogs;
